import { Component } from '@verndale/core';
import Moment from 'moment';
import { extendMoment } from 'moment-range';

const moment = extendMoment(Moment);

class Module extends Component {
  setupDefaults() {
    const {
      value,
      dataset: { minDate, maxDate }
    } = this.el;

    this.selectedDate = this.el.value ? moment(this.el.value) : null;
    this.currentMonth = this.el.value ? moment(this.el.value) : moment();
    this.minDate = minDate ? moment(minDate) : null;
    this.maxDate = maxDate ? moment(maxDate) : null;
    this.placeholderText = this.el.getAttribute('placeholder');

    this.buildDatepicker();
  }

  addListeners() {
    this.el.addEventListener('change', e => {
      e.target.dataset.date = e.target.value;
      this.handleSelectDate(e)
    })
  }

  buildDatepicker() {
    this.dom.wrapper = document.createElement('div');
    this.el.setAttribute('aria-hidden', true);
    this.el.setAttribute('tabindex', '-1');
    this.el.parentNode.insertBefore(this.dom.wrapper, this.el);
    this.dom.wrapper.classList.add('datepicker__wrapper');
    this.dom.wrapper.appendChild(this.el);

    // Add trigger
    this.dom.trigger = document.createElement('button');
    this.dom.trigger.type = 'button';
    this.dom.trigger.classList.add('datepicker__trigger');
    this.dom.wrapper.appendChild(this.dom.trigger);

    // Add placeholder
    this.dom.placeholder = document.createElement('span');
    this.dom.placeholder.classList.add('datepicker__placeholder');
    if (this.el.value) {
      const formatedDate = moment(this.el.value).format('MMMM D, YYYY');
      if (this.placeholderText) {
        this.dom.placeholder.innerHTML = `<span>${this.placeholderText}:</span>${formatedDate}`;
      } else {
        this.dom.placeholder.innerHTML = formatedDate;
      }
    } else if (this.placeholderText) {
      this.dom.placeholder.innerHTML = `<span>${this.placeholderText}</span>`;
    }

    const icon = document.createElement('svg');
    icon.classList.add('datepicker__icon');
    icon.innerHTML =
      '<svg viewBox="0 0 20 20"><use xlink:href="#booking-calendar"></use></svg>';

    this.dom.trigger.appendChild(this.dom.placeholder);
    this.dom.trigger.appendChild(icon);

    // Add panel
    const panel = document.createElement('div');
    this.dom.wrapper.appendChild(panel);

    panel.classList.add('date-picker__panel');
    panel.innerHTML = `
      <div class="date-picker">
        <div class="date-picker__body">
          <table class="date-picker__calendar" cellSpacing="0" cellPadding="0">
            <thead>
              <tr class="date-picker__nav">
                <th>
                  <button type="button" class="date-picker__nav-btn date-picker__nav-btn--prev">
                    <svg viewBox="0 0 180 320">
                      <use xlink:href="#chevron-right" />
                    </svg>
                  </button>
                </th>
                <th colSpan="5">
                  <p class="date-picker__nav-month"></p>
                </th>
                <th>
                  <button type="button" class="date-picker__nav-btn date-picker__nav-btn--next">
                    <svg viewBox="0 0 180 320">
                      <use xlink:href="#chevron-right" />
                    </svg>
                  </button>
                </th>
              </tr>
              <tr>
                <th>S</th>
                <th>M</th>
                <th>T</th>
                <th>W</th>
                <th>T</th>
                <th>F</th>
                <th>S</th>
              </tr>
            </thead>
            <tbody class="date-picker__calendar-body"></tbody>
          </table>
        </div>
      </div>
    `;

    this.dom.calendarMonth = panel.querySelector('.date-picker__nav-month');
    this.dom.calendarBody = panel.querySelector('.date-picker__calendar-body');
    this.dom.nextMonth = panel.querySelector('.date-picker__nav-btn--next');
    this.dom.prevMonth = panel.querySelector('.date-picker__nav-btn--prev');

    this.dom.trigger.addEventListener(
      'click',
      this.handleToggleCalendar.bind(this)
    );

    this.dom.nextMonth.addEventListener(
      'click',
      this.handleNextMonth.bind(this)
    );
    this.dom.prevMonth.addEventListener(
      'click',
      this.handlePreviousMonth.bind(this)
    );
    this.dom.calendarBody.addEventListener(
      'click',
      this.handleSelectDate.bind(this)
    );

    document.addEventListener('click', e => {
      if (
        e.target !== this.dom.wrapper &&
        !this.dom.wrapper.contains(e.target)
      ) {
        this.handleCloseCalendar();
      }
    });

    this.buildCalendar();
  }

  buildCalendar() {
    const month = this.buildMonth();

    const renderMonth = () =>
      month
        .map((week, i) => {
          const days = Array.from(week.by('day'));
          return `
            <tr>
            ${renderWeek(days)}
            </tr>
          `;
        })
        .join('');

    const renderWeek = days =>
      days
        .map(day => {
          let disabled = !moment(this.currentMonth).isSame(day, 'month');

          if (this.minDate) {
            disabled = disabled || this.minDate.diff(day, 'days') > 0;
          }

          if (this.maxDate) {
            disabled = disabled || this.maxDate.diff(day, 'days') < 0;
          }

          return `
            <td ${
              moment(day).isSame(this.selectedDate) ? 'class="selected"' : ''
            }>
              <button data-date="${day.format('YYYY-MM-DD')}" type="button" ${
            disabled ? 'disabled' : ''
          }>${day.date()}</button>
            </td>
          `;
        })
        .join('');

    this.dom.calendarMonth.innerHTML = moment(this.currentMonth).format(
      'MMMM YYYY'
    );
    this.dom.calendarBody.innerHTML = renderMonth();

    this.dom.prevMonth.disabled = this.minDate.isSame(
      this.currentMonth,
      'month'
    );
    this.dom.nextMonth.disabled = this.maxDate.isSame(
      this.currentMonth,
      'month'
    );
  }

  buildMonth() {
    const firstDay = moment(this.currentMonth).startOf('month');
    const endDay = moment(this.currentMonth).endOf('month');
    const monthRange = moment.range(firstDay, endDay);

    // Get all the weeks during the current month
    const weeks = [];
    Array.from(monthRange.by('days')).forEach(day => {
      if (!weeks.includes(day.week())) {
        weeks.push(day.week());
      }
    });

    while (weeks.length < 5) {
      weeks.push(weeks[weeks.length - 1] + 1);
    }

    const parsedWeeks = [];
    const currentYear = firstDay.year();
    const nextYear = currentYear + 1;

    weeks.forEach((week, index) => {
      parsedWeeks.push({
        week,
        year: (
          index > 0 &&
          (week < weeks[index -1] || parsedWeeks[index -1].year === nextYear)
        ) ? nextYear : currentYear
      })
    });

    const cal = [];
    parsedWeeks.forEach(({ week, year }) => {
      const firstWeekDay = moment().year(year).week(week).startOf('week');
      const lastWeekDay = moment().year(year).week(week).endOf('week');
      const weekRange = moment.range(firstWeekDay, lastWeekDay);
      cal.push(weekRange);
    });

    return cal;
  }

  handleToggleCalendar() {
    if (this.dom.wrapper.classList.contains('datepicker__wrapper--open')) {
      this.dom.wrapper.classList.remove('datepicker__wrapper--open');
    } else {
      this.dom.wrapper.classList.add('datepicker__wrapper--open');
    }
  }

  handleCloseCalendar() {
    if (this.dom.wrapper.classList.contains('datepicker__wrapper--open')) {
      this.dom.wrapper.classList.remove('datepicker__wrapper--open');
    }
  }

  handleSelectDate(e) {
    if (e.target.dataset.date) {
      let event;
      if (typeof Event === 'function') {
        event = new Event('dateChange');
      } else {
        event = document.createEvent('Event');
        event.initEvent('dateChange', true, true);
      }

      e.stopPropagation();
      this.selectedDate = moment(e.target.dataset.date);
      this.el.value = moment(this.selectedDate).format('YYYY-MM-DD');
      const formatedDate = moment(this.selectedDate).format('MMMM D, YYYY');

      if (this.placeholderText) {
        this.dom.placeholder.innerHTML = `<span>${this.placeholderText}:</span>${formatedDate}`;
      } else {
        this.dom.placeholder.innerHTML = formatedDate;
      }
      this.dom.wrapper.classList.remove('datepicker__wrapper--open');
      this.buildCalendar();

      this.el.dispatchEvent(event);
    }
  }

  handleNextMonth(e) {
    this.currentMonth = moment(this.currentMonth).add(1, 'month');
    this.buildCalendar();
  }

  handlePreviousMonth(e) {
    this.currentMonth = moment(this.currentMonth).subtract(1, 'month');
    this.buildCalendar();
  }

  on(event, callback) {
    this.el.addEventListener(event, callback);
  }

  update() {
    this.selectedDate = this.el.value ? moment(this.el.value) : null;
    this.currentMonth = this.el.value ? moment(this.el.value) : moment();

    this.buildCalendar();
  }
}

export default Module;
